import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowRight, User, Phone, MapPin, Mail, Edit2, Save, X, Plus,
  ShoppingBag, Clock, Trash2, ExternalLink, Printer, CheckCircle2, Circle, Check, Star, Wrench, RefreshCw,
} from 'lucide-react';
import api from '../utils/api';
import { getContrastTextColor } from '../utils/color';
import { formatPhone } from '../utils/phoneFormatter';
import { formatAddress } from '../utils/addressFormatter';
import { format, parseISO } from 'date-fns';
import OrderModal from '../components/orders/OrderModal';
import OrderDetailModal from '../components/orders/OrderDetailModal';
import ServiceCallModal from '../components/service_calls/ServiceCallModal';
import DocumentPreview from '../components/documents/DocumentPreview';

const STATUS_COLORS = { new: '#F59E0B', sent: '#3B82F6', completed: '#10B981' };
const STATUS_LABELS = { new: 'הזמנה חדשה', sent: 'נשלחה', completed: 'הסתיימה' };

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [printPreview, setPrintPreview] = useState(null);
  const [printLoadingId, setPrintLoadingId] = useState(null);

  async function handleDirectPrint(order) {
    setPrintLoadingId(order.id);
    try {
      const templatesRes = await api.get('/documents/templates');
      const tpl = templatesRes.data.find((t) => t.is_default_order) || templatesRes.data[0];
      if (!tpl) { alert('לא נמצאה תבנית הזמנה. צור תבנית בעמוד המסמכים.'); return; }
      const renderRes = await api.post('/documents/render', {
        template_id: tpl.id,
        customer_id: id,
        order_id: order.id,
      });
      setPrintPreview({
        rendered_html: renderRes.data.html,
        template_name: `הזמנה #${order.order_number}`,
      });
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה ביצירת מסמך');
    } finally {
      setPrintLoadingId(null);
    }
  }
  const [editingPhoneId, setEditingPhoneId] = useState(null);
  const [editingPhoneVal, setEditingPhoneVal] = useState('');
  const phoneInputRef = useRef(null);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelVal, setEditingLabelVal] = useState('');
  const labelInputRef = useRef(null);
  const [serviceCalls, setServiceCalls] = useState([]);
  const [showServiceCallModal, setShowServiceCallModal] = useState(false);
  const [editingServiceCall, setEditingServiceCall] = useState(null);

  const [interestTypes, setInterestTypes] = useState([]);
  const [showAddInterest, setShowAddInterest] = useState(false);
  const [googleSyncStatus, setGoogleSyncStatus] = useState(null); // null | 'syncing' | 'ok' | 'fail'

  const loadServiceCalls = useCallback(async () => {
    try {
      const res = await api.get('/service-calls', { params: { customer_id: id } });
      setServiceCalls(res.data.data || []);
    } catch { /* ignore */ }
  }, [id]);

  const loadCustomer = useCallback(async () => {
    try {
      const res = await api.get(`/customers/${id}`);
      setCustomer(res.data);
      setForm({
        name: res.data.name || '',
        email: res.data.email || '',
        city: res.data.city || '',
        street: res.data.street || '',
        street_number: res.data.street_number || '',
        apartment: res.data.apartment || '',
        floor: res.data.floor || '',
        postal_code: res.data.postal_code || '',
        notes: res.data.notes || '',
        referred_by_id: res.data.referred_by_id || '',
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCustomer();
    loadServiceCalls();
    api.get('/statuses').then((r) => setStatuses(r.data)).catch(() => {});
    api.get('/customers', { params: { limit: 200 } }).then((r) => setCustomers(r.data.data)).catch(() => {});
    api.get('/settings/interest-types').then((r) => setInterestTypes(r.data || [])).catch(() => {});
  }, [loadCustomer, loadServiceCalls]);

  async function saveCustomer() {
    try {
      await api.put(`/customers/${id}`, form);
      setEditing(false);
      loadCustomer();
    } catch (err) {
      alert('שגיאה בשמירה');
    }
  }

  async function changeStatus(statusId) {
    try {
      await api.put(`/customers/${id}/status`, { status_id: statusId, is_manual: true });
      loadCustomer();
    } catch {
      alert('שגיאה בעדכון סטטוס');
    }
  }

  async function resetToAutoStatus() {
    try {
      await api.post(`/customers/${id}/reset-status`);
      loadCustomer();
    } catch {
      alert('שגיאה באיפוס סטטוס');
    }
  }

  async function syncToGoogle() {
    setGoogleSyncStatus('syncing');
    try {
      const res = await api.post(`/customers/${id}/sync-google`);
      setGoogleSyncStatus(res.data.google_synced ? 'ok' : 'fail');
    } catch {
      setGoogleSyncStatus('fail');
    }
    setTimeout(() => setGoogleSyncStatus(null), 3000);
  }

  async function changeOrderStatus(orderId, status) {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      loadCustomer();
    } catch {
      alert('שגיאה בעדכון הזמנה');
    }
  }

  async function toggleOrderPaid(orderId, currentVal) {
    try {
      await api.put(`/orders/${orderId}/paid`, { is_paid: !currentVal });
      loadCustomer();
    } catch {
      alert('שגיאה בעדכון תשלום');
    }
  }

  async function deleteActivity(entryId) {
    if (!window.confirm('למחוק רשומה זו מההיסטוריה?')) return;
    try {
      await api.delete(`/customers/${id}/activity/${entryId}`);
      setCustomer((c) => ({ ...c, activity: c.activity.filter((a) => a.id !== entryId) }));
    } catch {
      alert('שגיאה במחיקה');
    }
  }

  async function deletePhone(phoneId) {
    if (!window.confirm('למחוק מספר טלפון זה?')) return;
    try {
      await api.delete(`/customers/${id}/phones/${phoneId}`);
      loadCustomer();
    } catch {
      alert('שגיאה במחיקה');
    }
  }

  async function addInterest(interestTypeId) {
    try {
      const it = interestTypes.find((x) => x.id === interestTypeId);
      await api.post(`/customers/${id}/interests`, { interest_type_id: interestTypeId });
      setCustomer((c) => ({
        ...c,
        interests: [...(c.interests || []), { id: interestTypeId, name: it?.name || '' }],
      }));
      setShowAddInterest(false);
    } catch { alert('שגיאה בהוספת תחום עניין'); }
  }

  async function removeInterest(interestTypeId) {
    try {
      await api.delete(`/customers/${id}/interests/${interestTypeId}`);
      setCustomer((c) => ({
        ...c,
        interests: (c.interests || []).filter((i) => i.id !== interestTypeId),
      }));
    } catch { alert('שגיאה בהסרת תחום עניין'); }
  }

  async function addPhone() {
    const phone = window.prompt('הזן מספר טלפון:');
    if (!phone) return;
    try {
      await api.post(`/customers/${id}/phones`, { phone });
      loadCustomer();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהוספת טלפון');
    }
  }

  function startEditPhone(p) {
    setEditingPhoneId(p.id);
    setEditingPhoneVal(p.phone);
    setTimeout(() => phoneInputRef.current?.focus(), 0);
  }

  async function savePhone(phoneId) {
    const val = editingPhoneVal.trim();
    if (!val) { setEditingPhoneId(null); return; }
    try {
      await api.put(`/customers/${id}/phones/${phoneId}`, { phone: val });
      loadCustomer();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בעדכון טלפון');
    }
    setEditingPhoneId(null);
  }

  function startEditLabel(p) {
    setEditingLabelId(p.id);
    setEditingLabelVal(p.label || '');
    setTimeout(() => labelInputRef.current?.focus(), 0);
  }

  async function saveLabel(phoneId) {
    const val = editingLabelVal.trim();
    try {
      await api.put(`/customers/${id}/phones/${phoneId}`, { label: val || null });
      loadCustomer();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בעדכון תווית');
    }
    setEditingLabelId(null);
  }

  async function setPrimary(phoneId) {
    try {
      await api.put(`/customers/${id}/phones/${phoneId}`, { is_primary: true });
      loadCustomer();
    } catch {
      alert('שגיאה בעדכון מספר ראשי');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!customer) return <div className="p-6 text-gray-400">לקוח לא נמצא</div>;

  const activityTypeLabel = {
    call: '📞 שיחה',
    order_created: '🛍️ הזמנה נפתחה',
    order_status: '📦 עדכון הזמנה',
    status_changed: '🔄 שינוי סטטוס',
    customer_created: '👤 לקוח נוצר',
    customer_updated: '✏️ פרטים עודכנו',
    document_created: '📄 מסמך נוצר',
    note: '📝 הערה',
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Back + title */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowRight size={18} />
        </button>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
          {customer.name || 'לקוח ללא שם'}
        </h1>
        {customer.status_name && (
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: customer.status_color || '#6B7280', color: getContrastTextColor(customer.status_color || '#6B7280') }}
          >
            {customer.status_name}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Customer details */}
        <div className="lg:col-span-1 space-y-4">

          {/* Contact card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 dark:text-white text-sm">פרטי קשר</h2>
              <div className="flex items-center gap-1">
                {/* Google sync button */}
                <button
                  onClick={syncToGoogle}
                  disabled={googleSyncStatus === 'syncing'}
                  title="שמור בגוגל אנשי קשר"
                  className={`p-1.5 rounded-lg transition-colors ${
                    googleSyncStatus === 'ok' ? 'text-green-600' :
                    googleSyncStatus === 'fail' ? 'text-red-500' :
                    'text-gray-400 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <RefreshCw size={15} className={googleSyncStatus === 'syncing' ? 'animate-spin' : ''} />
                </button>
                {!editing ? (
                  <button onClick={() => setEditing(true)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Edit2 size={15} />
                  </button>
                ) : (
                  <div className="flex gap-1">
                    <button onClick={saveCustomer}
                      className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20">
                      <Save size={15} />
                    </button>
                    <button onClick={() => setEditing(false)}
                      className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <X size={15} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {editing ? (
              <div className="space-y-3">
                {[
                  { key: 'name', label: 'שם' },
                  { key: 'email', label: 'מייל', ltr: true },
                  { key: 'city', label: 'עיר' },
                ].map(({ key, label, ltr }) => (
                  <div key={key}>
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{label}</label>
                    <input
                      value={form[key]}
                      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                      dir={ltr ? 'ltr' : undefined}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                                 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                ))}
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">רחוב</label>
                  <input value={form.street} onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="grid grid-cols-3 gap-2 col-span-1">
                  {[{ key: 'street_number', label: 'מספר' }, { key: 'apartment', label: 'דירה' }, { key: 'floor', label: 'קומה' }].map(({ key, label }) => (
                    <div key={key}>
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{label}</label>
                      <input value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">מיקוד</label>
                  <input value={form.postal_code} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">הגיע דרך</label>
                  <CustomerAutocomplete
                    value={form.referred_by_id}
                    onChange={(val) => setForm((f) => ({ ...f, referred_by_id: val }))}
                    customers={customers}
                    currentId={id}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">הערות</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                               bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <InfoRow icon={<User size={14} />} label="שם" value={customer.name} />
                <InfoRow icon={<Mail size={14} />} label="מייל" value={customer.email} ltr />
                <InfoRow icon={<MapPin size={14} />} label="עיר" value={customer.city} />
                {formatAddress(customer) && (
                  <InfoRow icon={<MapPin size={14} />} label="כתובת" value={formatAddress(customer)} />
                )}
                {customer.referred_by_name && (
                  <InfoRow icon={<User size={14} />} label="הגיע דרך"
                    value={
                      <button onClick={() => navigate(`/customers/${customer.referred_by_id}`)}
                        className="text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1">
                        {customer.referred_by_name} <ExternalLink size={11} />
                      </button>
                    }
                  />
                )}
                {customer.notes && (
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">הערות</p>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{customer.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Phones */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900 dark:text-white text-sm">טלפונים</h2>
              <button onClick={addPhone}
                className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20">
                <Plus size={15} />
              </button>
            </div>
            {customer.phones?.length === 0 ? (
              <p className="text-sm text-gray-400">אין מספרי טלפון</p>
            ) : (
              <div className="space-y-2">
                {customer.phones?.map((p) => (
                  <div key={p.id} className="flex items-center gap-1.5">
                    {/* Primary star */}
                    <button
                      onClick={() => !p.is_primary && setPrimary(p.id)}
                      title={p.is_primary ? 'מספר ראשי' : 'קבע כראשי'}
                      className={`p-1 rounded shrink-0 transition-colors ${p.is_primary ? 'text-amber-400 cursor-default' : 'text-gray-300 hover:text-amber-400'}`}
                    >
                      <Star size={12} fill={p.is_primary ? 'currentColor' : 'none'} />
                    </button>

                    <Phone size={12} className="text-gray-400 shrink-0" />

                    {/* Number + label stacked */}
                    <div className="flex-1 min-w-0">
                      {/* Phone number row */}
                      {editingPhoneId === p.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            ref={phoneInputRef}
                            value={editingPhoneVal}
                            onChange={(e) => setEditingPhoneVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') savePhone(p.id); if (e.key === 'Escape') setEditingPhoneId(null); }}
                            onBlur={() => savePhone(p.id)}
                            className="font-mono text-sm rounded border border-primary-400 px-2 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 w-32"
                            dir="ltr"
                          />
                          <button onMouseDown={(e) => { e.preventDefault(); savePhone(p.id); }}
                            className="p-0.5 text-emerald-500 hover:text-emerald-700">
                            <Check size={12} />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => startEditPhone(p)} title="לחץ לעריכה"
                          className="font-mono text-sm text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors block truncate"
                          dir="ltr">
                          {p.phone}
                        </button>
                      )}

                      {/* Label row */}
                      {editingLabelId === p.id ? (
                        <div className="flex items-center gap-1 mt-0.5">
                          <input
                            ref={labelInputRef}
                            value={editingLabelVal}
                            onChange={(e) => setEditingLabelVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveLabel(p.id); if (e.key === 'Escape') setEditingLabelId(null); }}
                            onBlur={() => saveLabel(p.id)}
                            placeholder="תווית..."
                            className="text-xs rounded border border-primary-400 px-2 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 w-28"
                          />
                          <button onMouseDown={(e) => { e.preventDefault(); saveLabel(p.id); }}
                            className="p-0.5 text-emerald-500 hover:text-emerald-700">
                            <Check size={11} />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => startEditLabel(p)} title="לחץ לעריכת תווית"
                          className="text-xs mt-0.5 block transition-colors hover:text-primary-600 dark:hover:text-primary-400">
                          {p.label
                            ? <span className="text-gray-400">{p.label}</span>
                            : <span className="text-gray-300 dark:text-gray-600">+ תווית</span>
                          }
                        </button>
                      )}
                    </div>

                    {/* Delete */}
                    <button onClick={() => deletePhone(p.id)}
                      className="p-1 rounded text-gray-300 hover:text-red-500 shrink-0">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Interests */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900 dark:text-white text-sm">תחומי עניין</h2>
              <button onClick={() => setShowAddInterest((v) => !v)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20">
                <Plus size={15} />
              </button>
            </div>

            {/* Tag badges */}
            <div className="flex flex-wrap gap-1.5">
              {(customer.interests || []).length === 0 && !showAddInterest && (
                <p className="text-sm text-gray-400">אין תחומי עניין</p>
              )}
              {(customer.interests || []).map((interest) => (
                <span key={interest.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                             bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                  {interest.name}
                  <button onClick={() => removeInterest(interest.id)}
                    className="ml-0.5 hover:text-red-500 transition-colors">
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>

            {/* Add dropdown */}
            {showAddInterest && (
              <div className="mt-2">
                <select
                  autoFocus
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) addInterest(e.target.value); }}
                  onBlur={() => setShowAddInterest(false)}
                  className="w-full px-3 py-1.5 text-sm rounded-lg border border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">בחר תחום עניין...</option>
                  {interestTypes
                    .filter((it) => !(customer.interests || []).some((ci) => ci.id === it.id))
                    .map((it) => <option key={it.id} value={it.id}>{it.name}</option>)
                  }
                </select>
              </div>
            )}
          </div>

          {/* Status change */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h2 className="font-semibold text-gray-900 dark:text-white text-sm mb-3">שינוי סטטוס</h2>
            <div className="flex flex-wrap gap-2">
              {statuses.map((s) => (
                <button
                  key={s.id}
                  onClick={() => changeStatus(s.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium text-white transition-opacity
                    ${customer.status_id === s.id ? 'opacity-100 ring-2 ring-offset-1 ring-gray-300' : 'opacity-70 hover:opacity-100'}`}
                  style={{ backgroundColor: s.color }}
                >
                  {s.name}
                </button>
              ))}
            </div>
            {customer.status_is_manual && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-2">
                ✋ סטטוס מוגדר ידנית — לא יעודכן אוטומטית
                <button
                  onClick={resetToAutoStatus}
                  className="underline hover:no-underline text-amber-700 dark:text-amber-300"
                >
                  בטל
                </button>
              </p>
            )}
          </div>
        </div>

        {/* RIGHT: Orders + Activity */}
        <div className="lg:col-span-2 space-y-4">
          {/* Orders */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <ShoppingBag size={16} />
                הזמנות ({customer.orders?.length || 0})
              </h2>
              <button
                onClick={() => setShowOrderModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700
                           text-white text-xs font-medium transition-colors"
              >
                <Plus size={13} />
                הזמנה חדשה
              </button>
            </div>

            {customer.orders?.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-400">אין הזמנות</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {customer.orders?.map((order) => (
                  <div
                    key={order.id}
                    onClick={() => setSelectedOrderId(order.id)}
                    className={`px-5 py-4 flex items-center justify-between gap-4 cursor-pointer transition-colors ${order.status === 'completed' && !order.is_paid ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
                  >
                    <div>
                      <div className="font-medium text-sm text-primary-600 dark:text-primary-400">
                        הזמנה #{order.order_number}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {format(parseISO(order.created_at), 'dd/MM/yyyy HH:mm')}
                      </div>
                      {order.status === 'completed' && !order.is_paid && (
                        <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400">
                          <span>⚠</span> הזמנה טרם שולמה!
                        </div>
                      )}
                      {order.notes && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm truncate">{order.notes}</div>
                      )}
                      {order.total_amount && (
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-1">
                          ₪{Number(order.total_amount).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => toggleOrderPaid(order.id, order.is_paid)}
                        title={order.is_paid ? 'סמן כלא שולם' : 'סמן כשולם'}
                        className={`p-1.5 rounded-lg transition-colors ${order.is_paid ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50' : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'}`}
                      >
                        {order.is_paid ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                      </button>
                      <select
                        value={order.status}
                        onChange={(e) => changeOrderStatus(order.id, e.target.value)}
                        className="text-xs px-2 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary-500"
                        style={{
                          borderColor: STATUS_COLORS[order.status] || '#6B7280',
                          color: STATUS_COLORS[order.status] || '#6B7280',
                        }}
                      >
                        <option value="new">הזמנה חדשה</option>
                        <option value="sent">נשלחה</option>
                        <option value="completed">הסתיימה</option>
                      </select>
                      <button
                        onClick={() => handleDirectPrint(order)}
                        disabled={printLoadingId === order.id}
                        title="הדפסה"
                        className="p-1.5 rounded-lg text-primary-500 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50"
                      >
                        {printLoadingId === order.id
                          ? <span className="h-3 w-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin inline-block" />
                          : <Printer size={14} />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Service Calls */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Wrench size={16} />
                קריאות שירות ({serviceCalls.length})
              </h2>
              <button
                onClick={() => { setEditingServiceCall(null); setShowServiceCallModal(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700
                           text-white text-xs font-medium transition-colors"
              >
                <Plus size={13} />
                קריאה חדשה
              </button>
            </div>

            {serviceCalls.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-400">אין קריאות שירות</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {serviceCalls.map((sc) => (
                  <div
                    key={sc.id}
                    onClick={() => { setEditingServiceCall(sc); setShowServiceCallModal(true); }}
                    className="px-5 py-3 flex items-start justify-between gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {sc.type_name && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            {sc.type_name}
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          sc.status === 'open'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {sc.status === 'open' ? 'פתוחה' : 'סגורה'}
                        </span>
                      </div>
                      {sc.details && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{sc.details}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {format(parseISO(sc.created_at), 'dd/MM/yyyy HH:mm')}
                      </p>
                    </div>
                    <Edit2 size={13} className="text-gray-300 dark:text-gray-600 shrink-0 mt-1" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Activity timeline */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock size={16} />
                היסטוריית התקשרויות
              </h2>
            </div>

            {customer.activity?.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-400">אין פעילות</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto">
                {customer.activity?.map((a) => (
                  <div key={a.id} className="px-5 py-3 flex items-start gap-3 group">
                    <span className="text-base mt-0.5">
                      {activityTypeLabel[a.type]?.split(' ')[0] || '•'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white">{a.description}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {format(parseISO(a.created_at), 'dd/MM/yyyy HH:mm')}
                        {a.performed_by_name && ` · ${a.performed_by_name}`}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteActivity(a.id)}
                      title="מחק רשומה"
                      className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-300 hover:text-red-500 transition-all shrink-0 mt-0.5"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showOrderModal && (
        <OrderModal
          customerId={id}
          customerName={customer.name}
          onClose={() => setShowOrderModal(false)}
          onSaved={() => { setShowOrderModal(false); loadCustomer(); }}
        />
      )}

      {printPreview && (
        <DocumentPreview doc={printPreview} onClose={() => setPrintPreview(null)} />
      )}

      {selectedOrderId && (
        <OrderDetailModal
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onStatusChanged={loadCustomer}
        />
      )}

      {showServiceCallModal && (
        <ServiceCallModal
          customerId={id}
          call={editingServiceCall}
          onClose={() => { setShowServiceCallModal(false); setEditingServiceCall(null); }}
          onSaved={() => { setShowServiceCallModal(false); setEditingServiceCall(null); loadServiceCalls(); }}
        />
      )}
    </div>
  );
}

function CustomerAutocomplete({ value, onChange, customers, currentId }) {
  const [inputText, setInputText] = useState('');
  const [open, setOpen] = useState(false);

  // Sync display text when the customers list loads or value changes
  useEffect(() => {
    const found = customers.find((c) => c.id === value);
    setInputText(found?.name || '');
  }, [value, customers]);

  const filtered = useMemo(() => {
    const q = inputText.trim().toLowerCase();
    return customers
      .filter((c) => c.id !== currentId && (!q || c.name.toLowerCase().includes(q)))
      .slice(0, 10);
  }, [inputText, customers, currentId]);

  function select(customer) {
    onChange(customer.id);
    setInputText(customer.name);
    setOpen(false);
  }

  function clear(e) {
    e.preventDefault();
    onChange('');
    setInputText('');
    setOpen(false);
  }

  function handleBlur() {
    // Delay so onMouseDown on list items fires first
    setTimeout(() => {
      setOpen(false);
      const found = customers.find((c) => c.id === value);
      setInputText(found?.name || '');
    }, 150);
  }

  return (
    <div className="relative">
      <input
        value={inputText}
        onChange={(e) => { setInputText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        placeholder="חפש לקוח..."
        className={`w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                   bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                   focus:outline-none focus:ring-2 focus:ring-primary-500 ${value ? 'pl-7' : ''}`}
      />
      {value && (
        <button
          type="button"
          onMouseDown={clear}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
          title="נקה בחירה"
        >
          <X size={12} />
        </button>
      )}
      {open && filtered.length > 0 && (
        <ul className="absolute z-30 top-full mt-1 w-full bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onMouseDown={() => select(c)}
                className="w-full text-right px-3 py-2 text-sm text-gray-900 dark:text-white hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value, ltr }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-gray-400 mt-0.5 shrink-0">{icon}</span>
      <div>
        <span className="text-xs text-gray-400 block">{label}</span>
        <span className="text-gray-900 dark:text-white" dir={ltr ? 'ltr' : undefined}>{value}</span>
      </div>
    </div>
  );
}
